export interface StatusStep {
  id: string
  kort: string
  label: string
  hue: number
  c: number
}

export const STATUS_PIPELINE: StatusStep[] = [
  { id: 'upplagd',      kort: 'Upplagd',           label: 'Upplagd',                   hue: 80,  c: 0.02 },
  { id: 'anmald',       kort: 'Anmäld',             label: 'Anmäld till best.',          hue: 250, c: 0.10 },
  { id: 'prissattning', kort: 'Under prissättn.',   label: 'Under prissättning',         hue: 75,  c: 0.13 },
  { id: 'prissatt',     kort: 'Prissatt',           label: 'Prissatt',                   hue: 295, c: 0.12 },
  { id: 'overlamnad',   kort: 'Överlämnad',         label: 'Prissatt – överlämnad',      hue: 215, c: 0.10 },
  { id: 'godkand',      kort: 'Godkänd',            label: 'Godkänd',                    hue: 145, c: 0.12 },
  { id: 'fakturerad',   kort: 'Fakturerad',         label: 'Fakturerad',                 hue: 80,  c: 0.04 },
]

export const STATUS_IDS: string[] = STATUS_PIPELINE.map((s) => s.id)

/** Built-in ids that carry business meaning (KPIs, default, spec-warnings).
 *  They can be reordered/recoloured but never deleted or renamed away. */
export const ANCHOR_STATUS_IDS = ['upplagd', 'godkand', 'fakturerad'] as const

/** Shape persisted in Project.statusConfig (JSON). All fields optional for
 *  back-compat with the original `{ hidden: [] }` configs. */
export interface StatusConfig {
  statuses?: StatusStep[]
  defaultStatus?: string
  hidden?: string[]
}

/** Effective status list for a project: its custom list when defined,
 *  otherwise the built-in seed pipeline. */
export const resolveStatuses = (config: StatusConfig | null | undefined): StatusStep[] =>
  config?.statuses && config.statuses.length > 0 ? config.statuses : STATUS_PIPELINE

export const defaultStatusId = (config: StatusConfig | null | undefined): string =>
  config?.defaultStatus || 'upplagd'

/** Look up a status in the given list (defaults to the seed). Unknown ids get a
 *  neutral fallback badge so renamed/removed statuses still render gracefully. */
export const statusInfo = (id: string, statuses: StatusStep[] = STATUS_PIPELINE): StatusStep =>
  statuses.find((s) => s.id === id)
    ?? STATUS_PIPELINE.find((s) => s.id === id)
    ?? { id, kort: id || '–', label: id || '–', hue: 80, c: 0.02 }

export const statusIndex = (id: string, statuses: StatusStep[] = STATUS_PIPELINE): number =>
  statuses.findIndex((s) => s.id === id)

type Hued = Pick<StatusStep, 'hue' | 'c'>

export const statusStepBg = ({ hue, c }: Hued): string =>
  `oklch(0.55 ${c} ${hue})`

export const statusBadgeBg = ({ hue, c }: Hued): string =>
  `oklch(0.95 ${Math.max(0.015, c * 0.25)} ${hue})`

export const statusBadgeText = ({ hue, c }: Hued): string =>
  `oklch(0.36 ${c} ${hue})`

export const statusDotColor = ({ hue, c }: Hued): string =>
  `oklch(0.55 ${c} ${hue})`

export const filterPipeline = (hidden: string[] = [], statuses: StatusStep[] = STATUS_PIPELINE): StatusStep[] =>
  statuses.filter((s) => !hidden.includes(s.id) || s.id === 'upplagd' || s.id === 'fakturerad')

/* Legacy – kept for any old code still referencing these */
export const STATUSES: string[] = STATUS_PIPELINE.map((s) => s.id)

export const STATUS_CLASS: Record<string, string> = {
  upplagd:      's-upplagd',
  anmald:       's-anmald',
  prissattning: 's-prissattning',
  prissatt:     's-prissatt',
  overlamnad:   's-overlamnad',
  godkand:      's-godkand',
  fakturerad:   's-fakturerad',
}
